import {View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {
  type ReaderFontSize,
  type ReaderLineHeight,
  type ReaderTheme,
  type ReaderWidth,
  useReaderSettings,
} from '#/lib/news/readerSettings'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Menu from '#/components/Menu'

const FONT_SIZE_LABELS: Record<ReaderFontSize, string> = {
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
}

const LINE_HEIGHT_LABELS: Record<ReaderLineHeight, string> = {
  snug: 'Tight',
  normal: 'Standard',
  relaxed: 'Airy',
}

const THEME_LABELS: Record<ReaderTheme, string> = {
  light: 'Light',
  dark: 'Dark',
  sepia: 'Sepia',
}

const WIDTH_LABELS: Record<ReaderWidth, string> = {
  narrow: 'Narrow',
  regular: 'Regular',
  wide: 'Wide',
}

export function ReaderToolbar() {
  const {_} = useLingui()
  const t = useTheme()
  const {
    fontSize,
    lineHeight,
    theme,
    width,
    setFontSize,
    setLineHeight,
    setTheme,
    setWidth,
  } = useReaderSettings()

  return (
    <Menu.Root>
      <Menu.Trigger label={_(msg`Reader settings`)}>
        {({props}) => (
          <Button
            {...props}
            label={props.accessibilityLabel}
            size="small"
            variant="ghost"
            color="secondary"
            shape="round"
            style={[a.px_sm]}>
            <ButtonText style={[t.atoms.text]}>AA</ButtonText>
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Outer>
        <View style={[a.gap_sm]}>
          <Menu.LabelText>{_(msg`Font size`)}</Menu.LabelText>
          <Menu.Group>
            {(Object.keys(FONT_SIZE_LABELS) as ReaderFontSize[]).map(key => (
              <Menu.Item
                key={key}
                label={FONT_SIZE_LABELS[key]}
                onPress={() => setFontSize(key)}>
                <Menu.ItemText>{FONT_SIZE_LABELS[key]}</Menu.ItemText>
                <Menu.ItemRadio selected={fontSize === key} />
              </Menu.Item>
            ))}
          </Menu.Group>

          <Menu.LabelText>{_(msg`Line height`)}</Menu.LabelText>
          <Menu.Group>
            {(Object.keys(LINE_HEIGHT_LABELS) as ReaderLineHeight[]).map(
              key => (
                <Menu.Item
                  key={key}
                  label={LINE_HEIGHT_LABELS[key]}
                  onPress={() => setLineHeight(key)}>
                  <Menu.ItemText>{LINE_HEIGHT_LABELS[key]}</Menu.ItemText>
                  <Menu.ItemRadio selected={lineHeight === key} />
                </Menu.Item>
              ),
            )}
          </Menu.Group>

          <Menu.LabelText>{_(msg`Theme`)}</Menu.LabelText>
          <Menu.Group>
            {(Object.keys(THEME_LABELS) as ReaderTheme[]).map(key => (
              <Menu.Item
                key={key}
                label={THEME_LABELS[key]}
                onPress={() => setTheme(key)}>
                <Menu.ItemText>{THEME_LABELS[key]}</Menu.ItemText>
                <Menu.ItemRadio selected={theme === key} />
              </Menu.Item>
            ))}
          </Menu.Group>

          <Menu.LabelText>{_(msg`Width`)}</Menu.LabelText>
          <Menu.Group>
            {(Object.keys(WIDTH_LABELS) as ReaderWidth[]).map(key => (
              <Menu.Item
                key={key}
                label={WIDTH_LABELS[key]}
                onPress={() => setWidth(key)}>
                <Menu.ItemText>{WIDTH_LABELS[key]}</Menu.ItemText>
                <Menu.ItemRadio selected={width === key} />
              </Menu.Item>
            ))}
          </Menu.Group>
        </View>
      </Menu.Outer>
    </Menu.Root>
  )
}
